import { Dispatch } from 'redux'
import { submitOrderAction } from 'shared/store/ducks/checkout/submitOrder'
import { isWebviewSelector } from 'shared/store/ducks/common'
import buildClientAPI from 'views/providers/clientAPI'
import { isEmpty } from 'shared/utils/objectUtils'
import { mapResponseToComponentDataStructure } from 'shared/propsMocks/paymentMethodData'
import * as CHECKOUT from 'shared/consts/checkout'
import gatherBrowserInfo from 'shared/utils/gatherBrowserInfo'
import { IState } from 'types/redux'
import {
  BlockedPaymentMethod,
  PaymentMethodEntry,
  Payment,
  AdyenPaymentRequest,
  AdyenPaymentDetailsRequest,
  PaymentResultCode
} from 'types/redux/checkout/payment'
import { updateOrderDraftAction } from './orderDraft'
import {
  addCheckoutErrorAction,
  getMountPointsInstance,
  getOrderDraftId,
  getCustomerId,
  getSubOrder
} from './common'
import { CREDIT_CARD } from 'views/consts/payments'
import { NO_CONTENT } from 'server/consts/codes'

// ---------------------------- HELPER -------------------------
export const getUrlPathForEnvironment = (
  state: IState,
  path: string
): string => {
  const rewriteUrlPathForCheckout = (mpMath, checkoutPathFragment) =>
    ['', checkoutPathFragment].concat(mpMath.split('/').splice(4)).join('/')
  let finalPath = path
  const isWebview = isWebviewSelector(state)

  switch (window.location.hostname) {
    case 'shop-apotheke-com-dev.redteclab.de':
    case 'shop-apotheke-com-qs.redteclab.de':
    case 'shop-apotheke.com':
    case 'fock-preview-server-com-d4cuojil3q-ew.a.run.app':
      finalPath = rewriteUrlPathForCheckout(
        path,
        `${isWebview ? 'webview/' : ''}new-checkout`
      )
      break
    case 'localhost':
      if (window.location.port === '8080') {
        finalPath = rewriteUrlPathForCheckout(
          path,
          `${isWebview ? 'webview/' : ''}new-checkout`
        )
      }
      break
    default: // Do nothing
  }

  return finalPath
}

export const getChannel = () => {
  const { userAgent } = window.navigator
  const [device] = userAgent.match(/(iPhone|iPad|iPod|Android)/) || []

  switch (device) {
    case 'iPhone':
    case 'iPad':
    case 'iPod':
      return 'iOS'
    case 'Android':
      return 'Android'
    default:
      return 'Web'
  }
}

// ---------------------------- ACTION TYPES -------------------------

export const REMOVE_PAYMENT_METHODS_ACTION: string =
  'REMOVE_PAYMENT_METHODS_ACTION'
export const FETCH_PAYMENT_METHODS_REQUEST: string =
  'FETCH_PAYMENT_METHODS_REQUEST'
export const FETCH_PAYMENTS_METHODS_SUCCESS: string =
  'FETCH_PAYMENT_METHODS_SUCCESS'
export const SELECT_DATA_SET_ID_FOR_PAYMENT: string =
  'SELECT_DATA_SET_ID_FOR_PAYMENT'
export const REMOVE_PAYMENT_RESULT: string = 'REMOVE_PAYMENT_RESULT'
export const PAYMENTS_SUCCESS: string = 'PAYMENTS_SUCCESS'
export const PAYMENT_DETAILS_SUCCESS: string = 'PAYMENT_DETAILS_SUCCESS'
export const PAYMENT_RESULT_CODE_ERROR: string = 'PAYMENT_RESULT_CODE_ERROR'
export const PAYMENT_SAVE_SUCCESS: string = 'PAYMENT_SAVE_SUCCESS'
export const SET_PAYMENT_METHOD_SUCCESS: string = 'SET_PAYMENT_METHOD_SUCCESS'
export const DISABLE_SUCCESS: string = 'DISABLE_SUCCESS'
export const SET_PAYMENT_AUTHORIZATION_LOADING: string =
  'SET_PAYMENT_AUTHORIZATION_LOADING'
export const SHOW_PAYMENT_ERROR: string = 'SHOW_PAYMENT_ERROR'
export const SHOW_NO_PAYMENT_METHODS_AVAILABLE_ERROR: string =
  'SHOW_NO_PAYMENT_METHODS_AVAILABLE_ERROR'

// ---------------------------- SELECTORS -------------------------

export const getPaymentMethods = (state: IState) =>
  state?.checkout?.paymentMethodEntries

export const getPaymentResult = (state: IState) =>
  state?.checkout?.paymentResult

export const getSubOrderPayment = (
  state: IState,
  subOrderId: string = CHECKOUT.SA_SELLER_ID
): Payment | { paymentKind: undefined } => {
  const subOrder = getSubOrder(state, subOrderId)
  const firstSubOrder = state?.checkout?.orderDraft?.subOrders[0]

  return (
    subOrder?.payment || firstSubOrder?.payment || { paymentKind: undefined }
  )
}

export const isPaymentAuthorizationLoading = (state: IState): boolean =>
  state.checkout?.isPaymentAuthorizationLoading || false

export const isPaymentLoading = (state: IState): boolean =>
  state.checkout?.isPaymentLoading || false

export const isPaymentServiceUnavailable = (state: IState): boolean => {
  return (
    (!isEmpty(state.checkout?.unavailableServices) &&
      !state.checkout?.unavailableServices.includes('os')) ||
    false
  )
}

export const getPaymentMethodFromOrderDraft = (state: IState): Payment => {
  try {
    const {
      checkout: {
        orderDraft: { subOrders }
      }
    } = state
    return subOrders?.map(it => it.payment)?.shift()
  } catch (e) {
    return null
  }
}

export const getPaymentPayload = (state: IState): AdyenPaymentRequest => {
  const customerNumber = getCustomerId(state)
  const orderDraftId = getOrderDraftId(state)
  const payment: Payment = getPaymentMethodFromOrderDraft(state)
  const browserInfo = gatherBrowserInfo(state)
  const path = getUrlPathForEnvironment(
    state,
    getMountPointsInstance().getCheckoutRedirectReturn()
  )
  const returnUrl = `${window.location.origin}${path}?odid=${orderDraftId}&osp=verify`
  const channel = getChannel()

  return {
    browserInfo,
    channel,
    origin: window.location.origin,
    paymentMethod: {
      type: payment?.paymentKind,
      storedPaymentMethodId: payment?.paymentData?.paymentDataId
    },
    reference: orderDraftId,
    returnUrl,
    shopperLocale: browserInfo?.language,
    shopperReference: customerNumber,
    storePaymentMethod: false
  }
}

export const hasBlockedPaymentsDueToRxProduct = (state: IState): boolean => {
  try {
    return (
      !isEmpty(state.checkout.blockedPaymentMethods) &&
      state.checkout.blockedPaymentMethods.some(entry =>
        entry.reason.endsWith('hasPrescriptionProduct')
      )
    )
  } catch {
    return false
  }
}

export const showPaymentErrorSelector = (state: IState): boolean =>
  state.checkout?.showPaymentError || false

export const showNoPaymentMethodsAvailableErrorSelector = (
  state: IState
): boolean => state.checkout?.showNoPaymentMethodsAvailableError || false

// ---------------------------- ACTIONS -------------------------

export const fetchPaymentMethodsRequest = () => ({
  type: FETCH_PAYMENT_METHODS_REQUEST
})

export const removePaymentMethodsAction = () => ({
  type: REMOVE_PAYMENT_METHODS_ACTION,
  payload: {
    blockedPaymentMethods: [],
    paymentMethodEntries: []
  }
})

export const fetchPaymentMethodsSuccessAction = ({
  paymentMethodEntries,
  blockedPaymentMethods,
  unavailableServices
}: {
  paymentMethodEntries: { [key: string]: PaymentMethodEntry }
  blockedPaymentMethods: BlockedPaymentMethod[]
  unavailableServices: string[]
}) => ({
  type: FETCH_PAYMENTS_METHODS_SUCCESS,
  blockedPaymentMethods,
  paymentMethodEntries,
  unavailableServices
})

export const removePaymentResultAction = () => ({
  type: REMOVE_PAYMENT_RESULT,
  paymentResult: undefined
})

export const paymentSuccessAction = paymentResult => ({
  type: PAYMENTS_SUCCESS,
  paymentResult
})

export const paymentDetailsSuccessAction = paymentResult => ({
  type: PAYMENT_DETAILS_SUCCESS,
  paymentResult
})

export const setPaymentMethodSuccessAction = payment => ({
  type: SET_PAYMENT_METHOD_SUCCESS,
  payment
})

export const disableSuccessAction = () => ({
  type: DISABLE_SUCCESS
})

export const setPaymentAuthorizationLoading = (isLoading: boolean) => {
  return {
    type: SET_PAYMENT_AUTHORIZATION_LOADING,
    isPaymentAuthorizationLoading: isLoading
  }
}

export const setPaymentResultCodeErrorAction = () => ({
  type: PAYMENT_RESULT_CODE_ERROR,
  paymentResult: { resultCode: PaymentResultCode.ERROR }
})

export const showPaymentErrorAction = (showPaymentError: boolean) => ({
  type: SHOW_PAYMENT_ERROR,
  showPaymentError
})

export const showNoPaymentMethodsAvailableErrorAction = (
  showNoPaymentMethodsAvailableError: boolean
) => ({
  type: SHOW_NO_PAYMENT_METHODS_AVAILABLE_ERROR,
  showNoPaymentMethodsAvailableError
})

// ---------------------------- THUNKS -------------------------
export const fetchPaymentMethodsAction = (): ((
  dispatch: Function,
  getState: () => IState
) => Promise<void>) => {
  const { paymentService } = buildClientAPI()
  return async (dispatch: Function, getState: () => IState) => {
    dispatch(fetchPaymentMethodsRequest())

    try {
      const state = getState()
      const orderDraftId = getOrderDraftId(state)
      const response = await paymentService.getPaymentMethods(orderDraftId)
      const {
        paymentMethodEntries,
        blockedPaymentMethods,
        unavailableServices
      } = mapResponseToComponentDataStructure(response.data)

      dispatch(
        fetchPaymentMethodsSuccessAction({
          paymentMethodEntries,
          blockedPaymentMethods,
          unavailableServices
        })
      )
    } catch (e) {
      dispatch(showNoPaymentMethodsAvailableErrorAction(true))
      dispatch(
        addCheckoutErrorAction(`fetchPaymentMethodsAction: ${e.message}`)
      )
    }
  }
}

export const setPaymentMethodAction = (
  payment: Payment
): ((dispatch: Function, getState: () => IState) => Promise<void>) => {
  const { checkoutService } = buildClientAPI()
  return async (dispatch: Function, getState: () => IState) => {
    try {
      const orderDraftId = getOrderDraftId(getState())
      const response = await checkoutService.updatePaymentMethod(
        orderDraftId,
        payment
      )
      const { data: orderDraft } = response

      dispatch(setPaymentMethodSuccessAction(payment))
      dispatch(updateOrderDraftAction(orderDraft, 'setPaymentMethodAction'))
      dispatch(fetchPaymentMethodsAction())
    } catch (e) {
      dispatch(addCheckoutErrorAction(`setPaymentMethodAction: ${e.message}`))
    }
  }
}

export const disableAction = (paymentDataId: string) => {
  const { paymentService } = buildClientAPI()
  return async (dispatch: Dispatch<any>, getState: () => IState) => {
    try {
      const state = getState()
      const shopperReference = getCustomerId(state)

      await paymentService.disablePaymentData({
        recurringDetailReference: paymentDataId,
        shopperReference
      })
      dispatch(disableSuccessAction())
      dispatch(fetchPaymentMethodsAction())
    } catch (e) {
      dispatch(addCheckoutErrorAction(`disableAction: ${e.message}`))
    }
  }
}

/**
 *  paymentAction for payment page to save payment data
 *  paymentAction for verification page to pay with payment data
 * @param paymentMethod Partial<AdyenPaymentRequest> if you provide the paymentMethod, payment data will additionally be saved
 */
export const paymentAction = (
  adyenPaymentRequest: Partial<AdyenPaymentRequest>
) => {
  const { paymentService } = buildClientAPI()
  return async (dispatch, getState) => {
    try {
      let paymentRequest = getPaymentPayload(getState())

      if (adyenPaymentRequest) {
        paymentRequest = {
          ...paymentRequest,
          ...adyenPaymentRequest
        }
        paymentRequest.storePaymentMethod = true
        paymentRequest.returnUrl = paymentRequest.returnUrl.replace(
          '=verify',
          '=payment'
        )
      } else if (paymentRequest.paymentMethod?.type === CREDIT_CARD) {
        paymentRequest.shopperInteraction = 'ContAuth'
        paymentRequest.recurringProcessingModel = 'CardOnFile'
      }

      const response = await paymentService.createPaymentData(
        paymentRequest.reference,
        paymentRequest
      )
      dispatch(paymentSuccessAction(response.data))

      if (
        response.status === NO_CONTENT &&
        !response.data?.unavailableServices?.includes('pmc') &&
        !response.data?.unavailableServices?.includes('sds')
      ) {
        dispatch(submitOrderAction())
      }
    } catch (e) {
      dispatch(setPaymentResultCodeErrorAction())
      dispatch(addCheckoutErrorAction(`paymentAction: ${e.message}`))
    }
  }
}

/**
 * paymentDetailsAction for payment page and verification page to finalize payment authentication
 * @param redirectData <AdyenPaymentDetailsRequest>
 */
export const paymentDetailsAction = (
  redirectData: Partial<AdyenPaymentDetailsRequest>
): ((dispatch: Function, getState: () => IState) => Promise<void>) => {
  const { paymentService } = buildClientAPI()
  return async (dispatch: Function, getState: () => IState) => {
    try {
      const state = getState()
      const orderDraftId = getOrderDraftId(state)
      const paymentDetailsRequest: AdyenPaymentDetailsRequest = {
        reference: orderDraftId,
        storePaymentMethod: false,
        ...redirectData
      }
      const response = await paymentService.handleRedirection(
        orderDraftId,
        paymentDetailsRequest
      )

      dispatch(paymentDetailsSuccessAction(response.data))
    } catch (e) {
      dispatch(setPaymentResultCodeErrorAction())
      dispatch(addCheckoutErrorAction(`paymentDetailsAction: ${e.message}`))
    }
  }
}
